import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  EyeOff,
  CloudOff,
  HelpCircle,
  ShieldCheck,
  Users,
  Target,
  Scroll,
  CheckCircle2,
  Mail,
} from "lucide-react";

import FadeIn from "@/components/FadeIn";
import NetworkDiagram from "@/components/NetworkDiagram";
import SeoHead from "@/components/SeoHead";
import InzichtenLinkBlock from "@/components/InzichtenLinkBlock";
import { ARTICLES } from "@/data/articles";

// Uitgelicht inzicht: standaard het meest actuele artikel, tenzij de bezoeker
// recent een dienst- of sectorpagina bekeek — dan het best passende artikel.
const MONTHS_NL = {
  januari: 1, februari: 2, maart: 3, april: 4, mei: 5, juni: 6,
  juli: 7, augustus: 8, september: 9, oktober: 10, november: 11, december: 12,
};
function updatedRank(s = "") {
  const [m, y] = s.toLowerCase().split(" ");
  return (parseInt(y, 10) || 0) * 100 + (MONTHS_NL[m] || 0);
}
function mostRecentArticle() {
  return [...ARTICLES].sort(
    (a, b) => updatedRank(b.lastUpdated) - updatedRank(a.lastUpdated)
  )[0];
}

// ---- Section 2: Urgency ----
const urgencyCards = [
  {
    icon: EyeOff,
    title: "AI gebruik zonder overzicht",
    text:
      "Medewerkers gebruiken AI tools, ingebouwde AI functies en algoritmische systemen vaak sneller dan beleid en toezicht kunnen volgen. Denk aan automatische beoordeling van parkeervergunningen, prioritering van Woo verzoeken, risicoselectie in de bijstand, ondersteuning bij woningtoewijzing of analyse van studentgegevens. Zonder overzicht is verantwoording aan bestuur, raad, toezichthouder of betrokkene kwetsbaar.",
  },
  {
    icon: CloudOff,
    title: "Cloudafhankelijkheid zonder bestuurlijke grip",
    text:
      "E mail, documenten, zaaksystemen, vergadertools en samenwerkingsomgevingen draaien steeds vaker in de cloud. Dat is praktisch, maar roept ook vragen op over toegang, datastromen, leveranciersmacht, juridische context en de mogelijkheid om op termijn over te stappen. Bestuurlijke regie begint met weten waar de afhankelijkheden zitten.",
  },
  {
    icon: HelpCircle,
    title: "Datarisico zonder regie",
    text:
      "Welke persoonsgegevens staan waar? Wie heeft toegang? Welke koppelingen bestaan met leveranciers of andere organisaties? Wat gebeurt er als data wordt buitgemaakt of verkeerd wordt gebruikt? Organisaties die deze vragen niet snel kunnen beantwoorden zijn kwetsbaar, juridisch, bestuurlijk en reputationeel.",
  },
];

// ---- Section 3: Products ----
const products = [
  {
    badge: "INSTAP",
    title: "Bestuursscan AI en Data",
    text:
      "Een eerste onafhankelijke bestuurlijke risicofoto van AI gebruik, cloudafhankelijkheden, algoritmen en datarisico's. De scan levert een helder overzicht, een score op de SovEU methode en een concrete actielijst.",
  },
  {
    badge: "VERDIEPING",
    title: "Regiebeeld AI, Cloud en Data",
    text:
      "Een diepgaande integrale analyse met rapport, risicomatrix en presentatie aan directie, bestuur of management. Het Regiebeeld laat zien waar de grootste bestuurlijke risico's en afhankelijkheden zitten.",
  },
  {
    badge: "INRICHTING",
    title: "Regiekader AI en Datagovernance",
    text:
      "Governance daadwerkelijk inrichten met AI beleid, dataclassificatie, AI register, algoritmeregister, leveranciersbeoordeling, rollenmodel en bestuurlijke incidentvoorbereiding.",
  },
  {
    badge: "STRUCTUREEL",
    title: "RegieBorging",
    text:
      "Doorlopende borging voor bestaande klanten. Nieuwe ontwikkelingen, nieuwe tools, nieuwe risico's en nieuwe vragen worden periodiek beoordeeld zodat de organisatie actueel en aantoonbaar in control blijft.",
  },
];

// ---- Section 4: Why ----
const whyCols = [
  {
    icon: ShieldCheck,
    title: "Onafhankelijk en bestuurlijk",
    text:
      "SovEU kijkt niet vanuit één leverancier, één techniek of één standaard. Wij beoordelen wat een organisatie daadwerkelijk gebruikt, welke bestuurlijke vragen dat oproept en welke keuzes nodig zijn om controle te krijgen.",
  },
  {
    icon: Users,
    title: "Begrijpelijk voor bestuur en directie",
    text:
      "Rapporten en adviezen zijn geschreven voor gemeentesecretarissen, bestuurders, directies, CIO's, CISO's, juristen en toezichthouders. Geen technisch rapport dat in een la verdwijnt, maar bestuurlijke taal die gebruikt kan worden in besluitvorming.",
  },
  {
    icon: Target,
    title: "Van overzicht naar uitvoering",
    text:
      "SovEU stopt niet bij signaleren. De producten leiden naar concrete acties: een register, een beleidslijn, een besluitboom, een prioriteitenmatrix, een incidentdossier of een bestuurlijke notitie.",
  },
  {
    icon: Scroll,
    title: "Europese regels als uitgangspunt",
    text:
      "SovEU sluit aan bij de Europese richting rond AI, data, transparantie en verantwoording. Denk aan de AI verordening, AVG, NIS2, BIO, DORA waar relevant en de groeiende behoefte aan algoritmische transparantie.",
  },
];

// ---- Section 5: Algorithm register highlights ----
const registerHighlights = [
  {
    title: "Welke algoritmen gebruiken wij?",
    text:
      "Veel organisaties weten niet volledig welke algoritmische systemen formeel of informeel worden ingezet.",
  },
  {
    title: "Kunnen wij besluiten uitleggen?",
    text:
      "Als een burger, huurder, student of cliënt vraagt hoe een besluit tot stand kwam, moet de organisatie een begrijpelijk antwoord kunnen geven.",
  },
  {
    title: "Wie is verantwoordelijk?",
    text:
      "Zonder eigenaarschap blijft algoritmisch handelen zweven tussen IT, juridisch, beleid en uitvoering.",
  },
];

// ---- Section 6: Eight dimensions ----
const dimensions = [
  { n: "01", title: "Gebruik en doel", text: "Waarvoor wordt het systeem ingezet, door wie en in welke context?" },
  { n: "02", title: "Data en gevoeligheid", text: "Welke persoonsgegevens, vertrouwelijke gegevens of gevoelige data worden verwerkt?" },
  { n: "03", title: "Toegang en datastromen", text: "Wie kan erbij, waar gaat data naartoe en welke koppelingen bestaan?" },
  { n: "04", title: "Jurisdictie en leveranciersmacht", text: "Welke leverancier is betrokken, onder welk recht en met welke afhankelijkheden?" },
  { n: "05", title: "Transparantie en uitlegbaarheid", text: "Kan de organisatie uitleggen wat het systeem doet en welke invloed het heeft?" },
  { n: "06", title: "Menselijk toezicht en governance", text: "Wie beslist, wie controleert, wie grijpt in en wie is bestuurlijk eigenaar?" },
  { n: "07", title: "Lock in en continuïteit", text: "Kan de organisatie overstappen, herstellen of tijdelijk zonder het systeem functioneren?" },
  { n: "08", title: "Proportionaliteit en digitale voetafdruk", text: "Is het gebruik van AI of automatisering nodig, proportioneel en verantwoord gezien energiegebruik, maatschappelijke impact en bestuurlijke waarde?" },
];

// ---- Section 7: Audiences ----
const audiences = [
  { title: "Gemeenten", text: "Vraagstukken rondom AI gebruik, cloudafhankelijkheden en de digitale kant van processen zoals Woo verzoeken, parkeervergunningen, uitkeringsbeslissingen en handhaving." },
  { title: "Uitvoeringsorganisaties", text: "Datastromen, geautomatiseerde processen, burgercontact, toezicht en verantwoording." },
  { title: "GGD'en en veiligheidsregio's", text: "Gevoelige persoonsgegevens, crisisprocessen, informatiebeveiliging en bestuurlijke verantwoording." },
  { title: "Woningcorporaties", text: "Huurdersdata, inkomensgegevens, betalingsachterstanden, woningtoewijzing, cloudservices en reputatierisico." },
  { title: "Onderwijsinstellingen", text: "Studentgegevens, leerplatforms, AI gebruik door medewerkers en studenten, toetsing, begeleiding en datagovernance." },
  { title: "Advies en ingenieursbureaus", text: "Projectdata, klantdossiers, duurzaamheidsonderzoeken, AI gebruik in rapportages, data analyse en digitale voetafdruk." },
  { title: "Zorg en maatschappelijke organisaties", text: "Cliëntgegevens, gevoelige dossiers, AI ondersteuning, cloudafhankelijkheid en toezicht." },
  { title: "Besturen en directies", text: "Bestuurlijke rust, aantoonbare controle, prioriteiten en besluitvorming." },
];

export default function Home() {
  const featuredArticle = useMemo(() => {
    try {
      const slug = localStorage.getItem("soveu:lastTopicArticle");
      const match = slug && ARTICLES.find((a) => a.slug === slug);
      if (match) return match;
    } catch (e) {
      // localStorage niet beschikbaar — val terug op meest actuele
    }
    return mostRecentArticle();
  }, []);

  return (
    <div data-testid="page-home" className="pt-[96px]">
      <SeoHead
        title="SovEU | AI Act readiness, AI-governance en digitale regie"
        description="SovEU helpt publieke organisaties, semipublieke organisaties en bedrijven met AI Act readiness, AI-governance, AI-gebruik, risicoclassificatie en digitale regie."
        path="/"
      />
      {/* Organization + WebSite structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "SovEU",
            url: "https://soveu.nl/",
            logo: "https://soveu.nl/logo.png",
            email: "info@soveu.nl",
            description:
              "SovEU is een onafhankelijke adviespraktijk voor AI governance, datagovernance en digitale regie voor publieke en semipublieke organisaties.",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Utrecht",
              addressCountry: "NL",
            },
            identifier: {
              "@type": "PropertyValue",
              propertyID: "KVK",
              value: "42033105",
            },
            sameAs: ["https://www.linkedin.com/company/soveu/"],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "SovEU",
            url: "https://soveu.nl/",
            inLanguage: "nl-NL",
          }),
        }}
      />
      {/* ===== 1. Hero ===== */}
      <section
        data-testid="home-hero"
        className="relative overflow-hidden"
        style={{ backgroundColor: "#1E3A5F" }}
      >
        <div className="container-soveu section grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <FadeIn className="lg:col-span-7 text-white">
            <div
              className="inline-block text-[13px] font-medium tracking-[0.14em] uppercase mb-6"
              style={{ color: "#9DB6D1" }}
            >
              Grip op AI. Regie over data.
            </div>
            <h1 className="h1-display text-white mb-6">
              Grip op AI, cloud en datarisico's voordat ze een bestuurlijk probleem worden.
            </h1>
            <p className="body-lg text-white/75 max-w-2xl mb-9">
              SovEU maakt zichtbaar welke AI tools, cloudservices, algoritmische toepassingen en datasystemen een organisatie gebruikt, welke bestuurlijke risico's dat oplevert en welke governance nodig is om aantoonbaar regie te voeren. Waar relevant brengen wij ook Europese alternatieven in beeld die organisaties meer zeggenschap kunnen geven over hun eigen data.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary btn-on-navy">
                Plan een gesprek
                <ArrowRight size={18} />
              </Link>
              <Link to="/methode" data-testid="hero-cta-secondary" className="btn-primary btn-secondary-on-navy">
                Bekijk de aanpak
              </Link>
            </div>
            <p className="mt-7 text-[13px] text-white/55 max-w-xl">
              Voor gemeenten, uitvoeringsorganisaties, woningcorporaties, onderwijsinstellingen en maatschappelijke organisaties.
            </p>
          </FadeIn>

          <FadeIn delay={150} className="lg:col-span-5">
            <NetworkDiagram className="w-full h-auto max-w-[520px] mx-auto" />
          </FadeIn>
        </div>
      </section>

      {/* ===== 2. Urgency ===== */}
      <section className="section bg-white" data-testid="home-urgency">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mx-auto text-center mb-6">
            <h2 className="h2-display mb-6">
              Digitale risico's zijn geen IT vraagstuk meer
            </h2>
          </FadeIn>
          <FadeIn delay={80} className="max-w-3xl mx-auto text-center space-y-5 mb-6">
            <p className="body-lg text-[#334155]">
              Organisaties worden steeds vaker geconfronteerd met datalekken, vragen over algoritmisch handelen en bestuurlijke verantwoording die zij niet eenvoudig kunnen afleggen. Cloudservices dragen primaire processen, terwijl de juridische en organisatorische afhankelijkheden niet altijd zichtbaar zijn — ook niet wanneer die diensten worden geleverd door bedrijven die weliswaar datacentra en kantoren in Europa hebben, maar juridisch zijn gevestigd buiten de EU en daardoor onder niet Europese wetgeving vallen.
            </p>
            <p className="body-lg text-[#334155]">
              De vraag is niet alleen of de techniek veilig is. De vraag is ook of de organisatie kan uitleggen welke systemen worden gebruikt, welke data daarin staat, wie toegang heeft, welke algoritmen besluiten ondersteunen en welke governance is ingericht. Dat is het terrein van SovEU.
            </p>
          </FadeIn>

          <FadeIn delay={150} className="max-w-3xl mx-auto text-center mb-14">
            <Link
              to="/digitale-regie"
              data-testid="home-bestuurlijk-link"
              className="inline-flex items-center gap-1.5 text-[15px] font-medium underline-offset-4 hover:underline transition-colors"
              style={{ color: "#2D6F8F" }}
            >
              Lees meer over digitale regie en Europese alternatieven
              <ArrowRight size={16} />
            </Link>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6">
            {urgencyCards.map((c, i) => (
              <FadeIn key={c.title} delay={i * 100}>
                <article className="soveu-card p-8 h-full" data-testid={`urgency-card-${i + 1}`}>
                  <div
                    className="w-11 h-11 rounded-md inline-flex items-center justify-center mb-5"
                    style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                  >
                    <c.icon size={22} strokeWidth={1.75} />
                  </div>
                  <h3 className="h3-display mb-3">{c.title}</h3>
                  <p className="text-[#334155] leading-relaxed">{c.text}</p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 2b. AI Act readiness — SEO section ===== */}
      <section
        className="section bg-white border-t border-[#E5E9EF]"
        data-testid="home-ai-act-readiness"
      >
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-10">
            <div className="eyebrow mb-3">AI Act readiness</div>
            <h2 className="h2-display mb-6">
              AI Act readiness voor publieke organisaties, semipublieke organisaties en bedrijven
            </h2>
            <div className="space-y-5 body-lg text-[#334155] leading-relaxed">
              <p>
                Steeds meer organisaties gebruiken AI via ChatGPT, Microsoft Copilot, chatbots, algoritmen, planningssoftware, HR-tools, klantanalyses of AI-functionaliteiten in bestaande software. Daardoor ontstaan nieuwe vragen over transparantie, gegevensbescherming, interne beheersing en aantoonbaar verantwoord gebruik.
              </p>
              <p>
                SovEU helpt publieke organisaties, semipublieke organisaties en bedrijven om AI-gebruik gestructureerd in kaart te brengen. Niet met papieren schijnzekerheid of een juridische compliance-stempel, maar met praktische analyse, risicoclassificatie, governance-documentatie en concrete vervolgstappen.
              </p>
              <p>
                De focus ligt op AI Act readiness, AI-geletterdheid, AI-governance, verantwoord gebruik van generatieve AI en directie- of bestuursgerichte verantwoording.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Voor bedrijven en MKB",
                text:
                  "Voor bedrijven die AI gebruiken in processen, klantcontact, HR, software, planning, analyse of besluitvoorbereiding en willen weten welke risico's en verplichtingen voor hen relevant zijn.",
                cta: "Bekijk diensten voor bedrijven",
                to: "/diensten#ai-act-startcheck",
                testid: "home-ai-card-bedrijven",
              },
              {
                title: "Voor adviesbureaus en dienstverleners",
                text:
                  "Voor privacy-, IT-, HR-, compliance- en organisatieadviesbureaus die hun klanten willen ondersteunen bij AI Act readiness, AI-geletterdheid en AI-governance, eventueel met SovEU als specialistische partner.",
                cta: "Bekijk ondersteuning voor adviesbureaus",
                to: "/diensten#white-label",
                testid: "home-ai-card-adviesbureaus",
              },
              {
                title: "Voor publieke en semipublieke organisaties",
                text:
                  "Voor gemeenten, woningcorporaties, onderwijsinstellingen, zorgorganisaties en publieke samenwerkingen die bestuurlijke grip willen op AI-gebruik, algoritmen, data en digitale afhankelijkheden.",
                cta: "Bekijk publieke en semipublieke organisaties",
                to: "/voor-wie",
                testid: "home-ai-card-publiek",
              },
            ].map((c, i) => (
              <FadeIn key={c.title} delay={i * 80}>
                <article className="soveu-card p-7 h-full flex flex-col" data-testid={c.testid}>
                  <h3 className="h3-display mb-3">{c.title}</h3>
                  <p className="text-[#334155] leading-relaxed text-[15px] mb-5 flex-1">
                    {c.text}
                  </p>
                  <Link
                    to={c.to}
                    className="inline-flex items-center gap-1.5 text-[14px] font-medium hover:underline underline-offset-4 transition-colors"
                    style={{ color: "#2D6F8F" }}
                    data-testid={`${c.testid}-cta`}
                  >
                    {c.cta}
                    <ArrowRight size={15} />
                  </Link>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 3. What SovEU does ===== */}
      <section className="section" style={{ backgroundColor: "#F5F7FA" }} data-testid="home-products">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <h2 className="h2-display mb-5">Wat SovEU doet</h2>
            <p className="body-lg text-[#334155]">
              SovEU vertaalt digitale complexiteit naar bestuurlijk overzicht. Wij brengen AI gebruik, cloudafhankelijkheden, algoritmen, datastromen en governance in kaart en vertalen dat naar concrete keuzes, registers, beleid en acties.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6">
            {products.map((p, i) => (
              <FadeIn key={p.title} delay={i * 70}>
                <article className="soveu-card p-8 h-full flex flex-col" data-testid={`product-card-${i + 1}`}>
                  <span
                    className="inline-block self-start text-[11px] font-semibold tracking-[0.14em] uppercase px-2.5 py-1 rounded mb-5"
                    style={{ backgroundColor: "#F5F7FA", color: "#2D6F8F", border: "1px solid #E5E9EF" }}
                  >
                    {p.badge}
                  </span>
                  <h3 className="h3-display mb-3">{p.title}</h3>
                  <p className="text-[#334155] leading-relaxed flex-1">{p.text}</p>
                  <Link
                    to="/diensten"
                    className="mt-6 inline-flex items-center gap-2 text-sm font-medium"
                    style={{ color: "#2D6F8F" }}
                    data-testid={`product-link-${i + 1}`}
                  >
                    Meer over deze dienst
                    <ArrowRight size={16} />
                  </Link>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 4. Why ===== */}
      <section className="section bg-white" data-testid="home-why">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <h2 className="h2-display">Waarom SovEU</h2>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {whyCols.map((c, i) => (
              <FadeIn key={c.title} delay={i * 90}>
                <div data-testid={`why-col-${i + 1}`}>
                  <div
                    className="w-12 h-12 rounded-md inline-flex items-center justify-center mb-5"
                    style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                  >
                    <c.icon size={22} strokeWidth={1.75} />
                  </div>
                  <h3 className="h3-display mb-3">{c.title}</h3>
                  <p className="text-[#334155] leading-relaxed text-[15px]">{c.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={400}>
            <p
              className="mt-12 text-[15px] italic text-[#6B7280] leading-relaxed"
              data-testid="why-tagline"
            >
              Van compliance naar controle. Van afhankelijkheid naar regie.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ===== 4b. Herkenbare bestuurlijke situatie ===== */}
      <section
        className="section bg-white border-t border-[#E5E9EF]"
        data-testid="home-voorbeeld"
      >
        <div className="container-soveu">
          <FadeIn className="max-w-3xl">
            <div className="eyebrow mb-3">Voorbeeld</div>
            <h2 className="h2-display mb-6">Herkenbare bestuurlijke situatie</h2>
            <article className="soveu-card p-8 md:p-10">
              <p className="body-lg text-[#334155] leading-relaxed mb-7">
                Een publieke organisatie gebruikt Microsoft 365, enkele SaaS leveranciers, generatieve AI tools en meerdere systemen met persoonsgegevens. IT, privacy en informatiebeheer kennen elk een deel van het beeld, maar bestuur en directie missen een integraal overzicht van AI gebruik, datastromen, leveranciersafhankelijkheid en AVG risico's. SovEU helpt om dat bestuurlijk inzichtelijk te maken en te vertalen naar concrete keuzes.
              </p>
              <Link
                to="/diensten"
                className="inline-flex items-center gap-1.5 text-[15px] font-medium underline-offset-4 hover:underline transition-colors"
                style={{ color: "#2D6F8F" }}
                data-testid="home-voorbeeld-cta"
              >
                Bekijk hoe SovEU helpt
                <ArrowRight size={16} />
              </Link>
            </article>
          </FadeIn>
        </div>
      </section>

      {/* ===== 5. Algorithm register ===== */}
      <section
        className="section"
        style={{ backgroundColor: "#1E3A5F" }}
        data-testid="home-register"
      >
        <div className="container-soveu text-white">
          <FadeIn className="max-w-3xl mb-12">
            <div className="eyebrow mb-4" style={{ color: "#9DB6D1" }}>Algoritmeregister</div>
            <h2 className="h2-display text-white mb-5">
              Algoritmen in beeld
            </h2>
            <p className="body-lg text-white/75">
              Publieke organisaties gebruiken steeds vaker algoritmen bij dienstverlening, toezicht, vergunningen, handhaving, planning, risicoanalyse en informatieverwerking. Een algoritmeregister is geen administratieve bijlage. Het is een bestuurlijk verantwoordingsinstrument. Het laat zien welke algoritmen worden gebruikt, waarvoor zij worden ingezet, welke data zij verwerken, wie verantwoordelijk is en hoe menselijk toezicht is geregeld.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-5">
            {registerHighlights.map((h, i) => (
              <FadeIn key={h.title} delay={i * 100}>
                <div
                  className="p-7 h-full rounded-lg"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.16)",
                  }}
                  data-testid={`register-highlight-${i + 1}`}
                >
                  <h3 className="text-white text-[20px] font-semibold mb-3 leading-snug">
                    {h.title}
                  </h3>
                  <p className="text-white/75 leading-relaxed text-[15px]">{h.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={300} className="mt-12">
            <Link to="/methode" data-testid="register-cta" className="btn-primary btn-on-navy">
              Bekijk de SovEU methode
              <ArrowRight size={18} />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* ===== 6. Eight dimensions ===== */}
      <section className="section bg-white" data-testid="home-dimensions">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <div className="eyebrow mb-3">Methode</div>
            <h2 className="h2-display mb-5">De SovEU Regiemethode</h2>
            <p className="body-lg text-[#334155]">
              SovEU beoordeelt systemen, AI toepassingen, cloudservices, algoritmen en datasystemen op acht vaste dimensies. Per dimensie krijgt de organisatie een bestuurlijke beoordeling: groen, oranje of rood, met een korte toelichting en concrete vervolgstap.
            </p>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {dimensions.map((d, i) => (
              <FadeIn key={d.n} delay={i * 50}>
                <div className="soveu-card p-6 h-full" data-testid={`dimension-${d.n}`}>
                  <div
                    className="text-[13px] font-semibold mb-3"
                    style={{ color: "#2D6F8F" }}
                  >
                    {d.n}
                  </div>
                  <h3 className="text-[17px] font-semibold mb-2 leading-snug" style={{ color: "#1E3A5F" }}>
                    {d.title}
                  </h3>
                  <p className="text-[#334155] text-[14px] leading-relaxed">{d.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={300}>
            <p className="mt-10 max-w-3xl text-[14px] text-[#6B7280] leading-relaxed border-l-2 pl-4" style={{ borderColor: "#2D6F8F" }}>
              Binnen het Regiebeeld biedt SovEU een optioneel verdiepingsblok over proportionaliteit en digitale voetafdruk: is het gebruik van AI of automatisering nodig, proportioneel en verantwoord? Dit is een bestuurlijke duiding, geen formele CO2 audit of accountantsverklaring.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ===== 7. Audiences ===== */}
      <section className="section" style={{ backgroundColor: "#F5F7FA" }} data-testid="home-audiences">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <h2 className="h2-display mb-5">Voor wie SovEU werkt</h2>
            <p className="body-lg text-[#334155]">
              SovEU werkt voor publieke organisaties, semipublieke organisaties en bedrijven die gevoelige data verwerken, AI gebruiken of bestuurlijk en zakelijk moeten kunnen uitleggen hoe digitale systemen worden ingezet.
            </p>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {audiences.map((a, i) => (
              <FadeIn key={a.title} delay={i * 50}>
                <div className="soveu-card p-6 h-full" data-testid={`audience-card-${i + 1}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <CheckCircle2
                      size={18}
                      className="mt-1 flex-shrink-0"
                      style={{ color: "#2D6F8F" }}
                      strokeWidth={2}
                    />
                    <h3 className="text-[16px] font-semibold leading-snug" style={{ color: "#1E3A5F" }}>
                      {a.title}
                    </h3>
                  </div>
                  <p className="text-[#334155] text-[14px] leading-relaxed">{a.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 8. Mission ===== */}
      <section className="section bg-white" data-testid="home-mission">
        <div className="container-soveu max-w-3xl">
          <FadeIn>
            <div className="eyebrow mb-4">Missie</div>
            <h2 className="h2-display mb-7">
              Van compliance naar controle. Van afhankelijkheid naar regie.
            </h2>
          </FadeIn>
          <FadeIn delay={100} className="space-y-5 body-lg text-[#334155] leading-relaxed">
            <p>
              Een digitaal onafhankelijk Europa is geen ideaal. Het is een keuze. Die keuze begint niet bij grote woorden, maar bij praktische vragen. Welke systemen gebruiken wij? Welke data verwerken wij? Welke algoritmen ondersteunen besluiten? Welke leveranciers bepalen onze ruimte om te handelen? En kunnen wij dit uitleggen aan burgers, huurders, studenten, cliënten, toezichthouders en bestuur?
            </p>
            <p>
              SovEU is opgericht om organisaties te helpen die keuze concreet te maken. Niet door angst te verkopen, maar door overzicht, governance en bestuurlijke regie te organiseren. Publieke data verdient publieke verantwoordelijkheid. AI gebruik verdient uitlegbaarheid. Digitale afhankelijkheid verdient bewuste besluitvorming.
            </p>
          </FadeIn>
          <FadeIn delay={250} className="mt-8">
            <Link to="/over" data-testid="mission-cta" className="btn-primary btn-ghost">
              Lees meer over SovEU
              <ArrowRight size={18} />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* ===== 9. Uitgelicht inzicht ===== */}
      <section
        className="section bg-white border-t border-[#E5E9EF]"
        data-testid="home-featured-inzicht"
      >
        <div className="container-soveu max-w-3xl">
          <FadeIn>
            <div className="eyebrow mb-4">Uitgelicht inzicht</div>
            <article className="rounded-lg border border-[#E5E9EF] overflow-hidden">
              <div className="h-1.5" style={{ backgroundColor: "#2D6F8F" }} />
              <div className="p-8 md:p-10">
                <div
                  className="text-[12px] uppercase tracking-wide font-medium mb-3"
                  style={{ color: "#6B7280" }}
                >
                  {featuredArticle.category}
                </div>
                <h2 className="h2-display mb-4">{featuredArticle.title}</h2>
                <p className="body-lg text-[#334155] leading-relaxed mb-8">
                  {featuredArticle.summary}
                </p>
                <div className="flex flex-wrap items-center gap-5">
                  <Link
                    to={featuredArticle.to}
                    className="btn-primary btn-ghost"
                    data-testid="home-featured-cta"
                  >
                    Lees het artikel
                    <ArrowRight size={18} />
                  </Link>
                  <span className="text-[13px] text-[#6B7280]">
                    Bijgewerkt {featuredArticle.lastUpdated}
                  </span>
                </div>
              </div>
            </article>
          </FadeIn>
        </div>
      </section>

      {/* ===== 9b. Uit de inzichten — subtle cross-link ===== */}
      <InzichtenLinkBlock
        title="Uit de inzichten"
        intro="Verdiepende stukken over algoritmen, cloudafhankelijkheid en datarisico's voor publieke en semipublieke organisaties."
        excludeSlug={featuredArticle.slug}
        testid="home-inzichten-block"
      />

      {/* ===== 10. Final CTA ===== */}
      <section
        className="section"
        style={{ backgroundColor: "#1E3A5F" }}
        data-testid="home-final-cta"
      >
        <div className="container-soveu max-w-3xl text-center text-white">
          <FadeIn>
            <h2 className="h2-display text-white mb-5">
              Grip begint met een eerste gesprek
            </h2>
            <p className="body-lg text-white/75 mb-9">
              Wilt u weten waar uw organisatie staat op AI gebruik, cloudafhankelijkheid, algoritmen en datarisico's? Plan een gesprek over de Bestuursscan AI en Data of mail direct naar info@soveu.nl.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact" data-testid="final-cta-button" className="btn-primary btn-on-navy">
                Plan een gesprek
                <ArrowRight size={18} />
              </Link>
              <a
                href="mailto:info@soveu.nl?subject=Kennismaking%20SovEU"
                data-testid="final-cta-mail"
                className="inline-flex items-center gap-2 text-white/90 hover:text-white text-[15px] font-medium underline-offset-4 hover:underline transition-colors"
              >
                <Mail size={16} />
                Mail naar info@soveu.nl
              </a>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
